import { formatAmount } from "../../../../../utils/hooks/dataHandler";
import {
  autoTextColor,
  beautyAutoLine,
  boldAutoColumn,
} from "../../../../../utils/pdf/page";
import {
  buildAutoTable,
  enhanceReferenceColumn,
  formatReferenceDetails,
} from "../../../../../utils/pdf/buildAutoTable";
import { getYAConfigValue } from "../../../CrossYACalculationSummary";
import {
  getManualTaxPreviouslyAssessedRows,
  isAutoloadTaxPreviouslyAssessedRow,
  isManualTaxPreviouslyAssessedRowForYA,
  omitCrossYARowFields,
} from "../../shared";
import { resolveIrasChargeableIncomeSummary } from "../chargeableIncomeSummary";
import { addIrasLandscapePage, IRAS_TABLE_START_Y } from "../layoutConfig";
import {
  buildIrasSectionSpacer,
  getIrasTableCellPadding,
  getIrasTableStyles,
  normaliseIrasAutoTextColumnSpacing,
} from "../tableSpacing";
import {
  buildIrasCrossYAHeaders,
  getIrasCrossYAMainCompColumnStyles,
} from "./layoutConfig";

const MAX_MAIN_COMP_ROWS_ON_SHARED_PAGE = 6;
const YA_CONFIG = {
  preceding: {
    amountKey: "preceding_amount",
    summaryKey: "preceding",
  },
  current: {
    amountKey: "current_amount",
    summaryKey: "current",
  },
};

const selectYAAmountRows = (rows, amountKey) =>
  (rows || [])
    .filter(
      (row) =>
        isAutoloadTaxPreviouslyAssessedRow(row) ||
        isManualTaxPreviouslyAssessedRowForYA(row, amountKey),
    )
    .map((row) => {
      const amount = row?.[amountKey] ?? row?.amount;

      return {
        ...omitCrossYARowFields(row),
        amount,
        historyAmount: [amount],
      };
    });

const resolveYASummary = ({
  amountKey,
  chargeableIncomeBeforeExemptAmount,
  computedSummary,
  corporateIncomeTaxRebate,
  currentYearRebate,
  isStartup,
  summaryKey,
  taxPreviouslyAssessed,
}) => {
  const rebateConfig = getYAConfigValue(corporateIncomeTaxRebate, amountKey);
  const cashGrant = getYAConfigValue(currentYearRebate, amountKey);
  const startup = Boolean(getYAConfigValue(isStartup, amountKey));
  const selectedTaxPreviouslyAssessed = selectYAAmountRows(
    taxPreviouslyAssessed,
    amountKey,
  );
  const resolvedSummary = resolveIrasChargeableIncomeSummary({
    chargeableIncomeBeforeExemptAmount,
    computedSummary: computedSummary?.[summaryKey] || {},
    corporateIncomeTaxRebate: rebateConfig,
    currentYearRebate: cashGrant,
    isStartup: startup,
    taxPreviouslyAssessed: selectedTaxPreviouslyAssessed,
  });
  const previousTaxRows = getManualTaxPreviouslyAssessedRows(
    selectedTaxPreviouslyAssessed,
  );
  const previousTaxTotal = previousTaxRows.reduce(
    (total, row) => total + (parseFloat(row.amount) || 0),
    0,
  );

  return {
    amountKey,
    cashGrant,
    rebateConfig,
    startup,
    taxPreviouslyAssessed: selectedTaxPreviouslyAssessed,
    ...resolvedSummary,
    previousTaxRows,
    previousTaxTotal,
    netTaxPayable:
      resolvedSummary.postDeductions -
      Number(resolvedSummary.rebate.netRebate) +
      previousTaxTotal,
  };
};

const buildResultRow = (
  label,
  precedingAmount,
  currentAmount,
  showDecimal = false,
) => [
  boldAutoColumn(label),
  "",
  formatAmount(precedingAmount, showDecimal),
  "",
  formatAmount(currentAmount, showDecimal),
  "",
];

const buildDetailRow = ({
  currentAmount,
  label,
  precedingAmount,
  reference,
  schedule,
}) => ({
  cells: [
    label,
    precedingAmount,
    "",
    currentAmount,
    "",
    schedule && `${schedule}`,
  ],
  reference,
});

const buildTotalRow = (precedingAmount, currentAmount) => [
  "",
  "",
  precedingAmount,
  "",
  currentAmount,
  "",
];

const getExemptionTitle = (preceding, current) => {
  if (preceding.startup !== current.startup) return "Less: Tax Exemption";
  return preceding.startup
    ? "Less: Partial Tax Exemption Scheme for New Start-up Company"
    : "Less: Partial Tax Exemption";
};

const getYAName = (company, yaKey) => {
  const yearOfAssessment = Number(company?.yearOfAssessment);
  const year = Number.isFinite(yearOfAssessment)
    ? yearOfAssessment - (yaKey === "preceding" ? 1 : 0)
    : company?.yearOfAssessment || "";
  return `${yaKey === "preceding" ? "Preceding" : "Current"} YA ${year}`.trim();
};

const buildExemptionTierLabel = ({ company, summary, tier, yaKey }) => {
  const isFirstTier = tier === "first";
  const threshold = isFirstTier
    ? summary.exemption.firstThreshold
    : summary.exemption.secondThreshold;
  const cappedAmount = isFirstTier
    ? Math.min(summary.chargeableIncomeBeforeExemptAmount, threshold)
    : Math.min(
        threshold,
        Math.abs(
          summary.chargeableIncomeBeforeExemptAmount -
            summary.exemption.firstThreshold,
        ),
      );

  return `${getYAName(company, yaKey)} - ${
    isFirstTier ? "First" : "Next"
  } S$${formatAmount(cappedAmount)} @ ${isFirstTier ? "75%" : "50%"}`;
};

const hasSameRebateConfig = (preceding, current) =>
  preceding.rebateConfig?.taxableRate === current.rebateConfig?.taxableRate &&
  preceding.rebateConfig?.cappedAmount === current.rebateConfig?.cappedAmount;

const hasRebateConfig = (summary) =>
  Boolean(
    summary.rebateConfig?.taxableRate && summary.rebateConfig?.cappedAmount,
  );

const formatRebateLabel = (config = {}) =>
  `CIT rebate at ${config.taxableRate || 0}%, capped at S$${formatAmount(
    config.cappedAmount || 0,
  )}`;

const formatYARebateLabel = (company, yaKey, summary) =>
  `${getYAName(company, yaKey)}: ${
    hasRebateConfig(summary)
      ? formatRebateLabel(summary.rebateConfig)
      : "No CIT rebate"
  }`;

const getAppliedCashGrant = (summary) => {
  if (!summary.rebateConfig?.isRebate) return 0;

  return Math.min(
    Math.abs(summary.rebate.grossRebate || 0),
    Math.abs(summary.cashGrant?.amount || 0),
  );
};

export const renderIrasCrossYAChargeableIncomeTable = ({
  chargeableIncomeBeforeExemptAmount = {},
  company,
  computedSummary = {},
  corporateIncomeTaxRebate = {},
  currentYearRebate = {},
  forView,
  isCompact = false,
  isStartup,
  mainCompBodyRowCount = 0,
  pdf,
  reviewNote = "",
  schedule,
  tableSpacingOptions,
  taxPreviouslyAssessed = [],
}) => {
  const summaries = Object.keys(YA_CONFIG).reduce((nextSummaries, yaKey) => {
    const config = YA_CONFIG[yaKey];
    nextSummaries[yaKey] = resolveYASummary({
      ...config,
      chargeableIncomeBeforeExemptAmount:
        chargeableIncomeBeforeExemptAmount?.[yaKey],
      computedSummary,
      corporateIncomeTaxRebate,
      currentYearRebate,
      isStartup,
      taxPreviouslyAssessed,
    });
    return nextSummaries;
  }, {});
  const { preceding, current } = summaries;
  const body = [];
  const totalLines = [];
  const totalSubLines = [];
  const totalDoubleLines = [];
  const columnCount = 6 + (forView ? 1 : 0);
  const spacingOptions = tableSpacingOptions ?? isCompact;
  const buildReferenceCell = (reference) =>
    autoTextColor(
      reference,
      [255, 0, 0],
      true,
      getIrasTableCellPadding(spacingOptions),
    );
  const pushDetailRow = (row) => {
    if (forView) row.cells.push(buildReferenceCell(row.reference));
    body.push(row.cells);
  };
  const hasChargeableIncome = [preceding, current].some(
    (summary) => summary.chargeableIncomeBeforeExemptAmount > 0,
  );

  body.push(
    buildResultRow(
      hasChargeableIncome
        ? "Chargeable Income Before Exempt Amount"
        : "Chargeable Income",
      preceding.chargeableIncomeBeforeExemptAmount,
      current.chargeableIncomeBeforeExemptAmount,
      true,
    ),
  );

  if (!hasChargeableIncome) {
    totalSubLines.push(body.length - 1);
    totalDoubleLines.push(body.length - 1);
  }
  body.push(buildIrasSectionSpacer(columnCount, spacingOptions));

  if (hasChargeableIncome) {
    body.push([boldAutoColumn(getExemptionTitle(preceding, current))]);

    Object.keys(YA_CONFIG).forEach((yaKey) => {
      const summary = summaries[yaKey];
      if (summary.chargeableIncomeBeforeExemptAmount <= 0) return;

      const isPreceding = yaKey === "preceding";
      const buildTierRow = (tier, amount) =>
        buildDetailRow({
          label: buildExemptionTierLabel({ company, summary, tier, yaKey }),
          precedingAmount: isPreceding ? formatAmount(-amount) : "N.A.",
          currentAmount: isPreceding ? "N.A." : formatAmount(-amount),
        });

      pushDetailRow(buildTierRow("first", summary.firstExemptAmount));
      if (summary.secondExemptAmount > 0) {
        pushDetailRow(buildTierRow("second", summary.secondExemptAmount));
      }
    });
    totalLines.push(body.length - 1);

    body.push(
      buildTotalRow(
        formatAmount(
          -(preceding.firstExemptAmount + preceding.secondExemptAmount),
        ),
        formatAmount(-(current.firstExemptAmount + current.secondExemptAmount)),
      ),
    );
    totalSubLines.push(body.length - 1);

    body.push(
      buildResultRow(
        "Chargeable Income After Exempt Amount",
        preceding.chargeableIncomeAfterExemptAmount,
        current.chargeableIncomeAfterExemptAmount,
        true,
      ),
    );
    totalSubLines.push(body.length - 1);
    totalDoubleLines.push(body.length - 1);
    body.push(buildIrasSectionSpacer(columnCount, spacingOptions));
  }

  body.push([]);
  body.push(
    buildResultRow(
      "Tax Payable at 17%",
      preceding.taxableIncome,
      current.taxableIncome,
    ),
  );

  const autoloadTaxSetOffRows = taxPreviouslyAssessed.filter(
    (row) => row.status === "autoload",
  );
  const hasAutoloadTaxSetOff = autoloadTaxSetOffRows.length > 0;
  const previousTaxRows = getManualTaxPreviouslyAssessedRows(
    taxPreviouslyAssessed,
  );
  const hasManualPreviousTax = previousTaxRows.length > 0;
  const hasRebate = [preceding, current].some(hasRebateConfig);
  const shouldShowTaxPayableAfterSetOff =
    hasAutoloadTaxSetOff && (hasRebate || hasManualPreviousTax);

  if (hasAutoloadTaxSetOff) {
    body.push(buildIrasSectionSpacer(columnCount, spacingOptions));
    body.push([boldAutoColumn("Less: Tax deductions")]);

    autoloadTaxSetOffRows.forEach((row) => {
      const shouldDisplayAsTotal = autoloadTaxSetOffRows.length === 1;
      const formatTaxAmount = (amount) =>
        row.taxAdjustment === "Deduct"
          ? formatAmount(Math.abs(amount), false, true)
          : formatAmount(Math.abs(amount), false);
      const precedingAmount = formatTaxAmount(
        row.preceding_amount ?? row.amount,
      );
      const currentAmount = formatTaxAmount(row.current_amount ?? row.amount);
      const detailRow = buildDetailRow({
        label: `${row.name}`,
        precedingAmount: shouldDisplayAsTotal ? "" : precedingAmount,
        currentAmount: shouldDisplayAsTotal ? "" : currentAmount,
        schedule: row.schedule,
        reference: row.reference,
      });

      if (shouldDisplayAsTotal) {
        detailRow.cells[2] = precedingAmount;
        detailRow.cells[4] = currentAmount;
      }
      pushDetailRow(detailRow);
    });

    if (autoloadTaxSetOffRows.length > 1) {
      totalLines.push(body.length - 1);
      body.push(
        buildTotalRow(
          formatAmount(preceding.autoloadTaxSetOff.toFixed(2), false),
          formatAmount(current.autoloadTaxSetOff.toFixed(2), false),
        ),
      );
      totalSubLines.push(body.length - 1);
    }

    if (shouldShowTaxPayableAfterSetOff) {
      if (autoloadTaxSetOffRows.length === 1) {
        totalSubLines.push(body.length - 1);
      }
      body.push(
        buildResultRow(
          "Tax Payable after Set Off",
          preceding.postDeductions,
          current.postDeductions,
        ),
      );
    }
  }

  if (hasRebate) {
    body.push(buildIrasSectionSpacer(columnCount, spacingOptions));
    body.push([boldAutoColumn("Less: Tax Rebate")]);

    const sameRebateConfig = hasSameRebateConfig(preceding, current);
    const appliedCashGrant = {
      preceding: getAppliedCashGrant(preceding),
      current: getAppliedCashGrant(current),
    };
    const hasCashGrant = Object.values(appliedCashGrant).some(
      (amount) => amount > 0,
    );
    const pushRebateRow = (yaKey) => {
      const summary = summaries[yaKey];
      const isPreceding = yaKey === "preceding";
      const amount = hasRebateConfig(summary) ? -summary.rebate.grossRebate : 0;
      const rebateRow = buildDetailRow({
        label: formatYARebateLabel(company, yaKey, summary),
        precedingAmount: isPreceding ? formatAmount(amount, false) : "N.A.",
        currentAmount: isPreceding ? "N.A." : formatAmount(amount, false),
      });

      if (!hasCashGrant) {
        rebateRow.cells[2] = rebateRow.cells[1];
        rebateRow.cells[1] = "";
        rebateRow.cells[4] = rebateRow.cells[3];
        rebateRow.cells[3] = "";
      }
      pushDetailRow(rebateRow);
    };

    if (sameRebateConfig) {
      const rebateRow = buildDetailRow({
        label: formatRebateLabel(preceding.rebateConfig),
        precedingAmount: formatAmount(-preceding.rebate.grossRebate, false),
        currentAmount: formatAmount(-current.rebate.grossRebate, false),
      });

      if (!hasCashGrant) {
        rebateRow.cells[2] = rebateRow.cells[1];
        rebateRow.cells[1] = "";
        rebateRow.cells[4] = rebateRow.cells[3];
        rebateRow.cells[3] = "";
      }
      pushDetailRow(rebateRow);
    } else {
      Object.keys(YA_CONFIG).forEach(pushRebateRow);
    }

    if (hasCashGrant) {
      pushDetailRow(
        buildDetailRow({
          label: "CIT Rebate Cash Grant",
          precedingAmount: formatAmount(appliedCashGrant.preceding, false),
          currentAmount: formatAmount(appliedCashGrant.current, false),
        }),
      );
      totalLines.push(body.length - 1);
      body.push(
        buildTotalRow(
          formatAmount(-preceding.rebate.netRebate, false),
          formatAmount(-current.rebate.netRebate, false),
        ),
      );
    }

    totalSubLines.push(body.length - 1);
    if (hasManualPreviousTax) {
      body.push(
        buildResultRow(
          "Tax payable before ECI",
          preceding.postDeductions - Number(preceding.rebate.netRebate),
          current.postDeductions - Number(current.rebate.netRebate),
        ),
      );
    }
  }

  if (hasManualPreviousTax) {
    body.push(buildIrasSectionSpacer(columnCount, spacingOptions));
    previousTaxRows.forEach(({ label, row }) => {
      const formatYAAmount = (amountKey) =>
        isManualTaxPreviouslyAssessedRowForYA(row, amountKey)
          ? formatAmount(-Math.abs(row[amountKey] ?? row.amount ?? 0), false)
          : "";

      body.push([
        boldAutoColumn(label),
        "",
        formatYAAmount("preceding_amount"),
        "",
        formatYAAmount("current_amount"),
        "",
      ]);
    });
  }

  if (hasAutoloadTaxSetOff || hasRebate || hasManualPreviousTax) {
    totalSubLines.push(body.length - 1);
  }

  body.push(
    buildResultRow(
      "Net Tax Payable / (Tax Refund)",
      preceding.netTaxPayable,
      current.netTaxPayable,
    ),
  );
  totalSubLines.push(body.length - 1);
  totalDoubleLines.push(body.length - 1);

  if (forView && body.length > 0) {
    const { body: enhancedBody, referenceDetails } = enhanceReferenceColumn(
      body,
      6,
      15,
    );
    body.length = 0;
    body.push(...enhancedBody);
    normaliseIrasAutoTextColumnSpacing(body, 6, spacingOptions);

    const refDetailsText = formatReferenceDetails(referenceDetails);
    if (refDetailsText) {
      if (reviewNote !== "") reviewNote += "\n\n";
      reviewNote += "[Reference Details]\n" + refDetailsText;
    }
  }

  const shouldStartOnNewPage =
    !pdf.isExcel && mainCompBodyRowCount > MAX_MAIN_COMP_ROWS_ON_SHARED_PAGE;
  if (shouldStartOnNewPage) addIrasLandscapePage(pdf);

  const tableStartY = shouldStartOnNewPage
    ? IRAS_TABLE_START_Y
    : pdf.startY || IRAS_TABLE_START_Y;
  const detailLineColumns = [1, 3];
  const totalLineColumns = [2, 4];

  buildAutoTable({
    head: buildIrasCrossYAHeaders({ company, forView }),
    body,
    pdf,
    customStartY: tableStartY,
    schedule: { ...schedule },
    company,
    styles: getIrasTableStyles(pdf, spacingOptions),
    columnStyles: getIrasCrossYAMainCompColumnStyles(false, forView),
    didDrawCell: function (data) {
      beautyAutoLine(
        data,
        pdf.doc,
        (totalLines.includes(data.row.index) &&
          detailLineColumns.includes(data.column.index)) ||
          (totalSubLines.includes(data.row.index) &&
            totalLineColumns.includes(data.column.index)),
        totalDoubleLines.includes(data.row.index) &&
          totalLineColumns.includes(data.column.index),
      );
    },
    lineSettings: {
      totalLines: totalSubLines,
      totalDoubleLines,
      subTotalLines: totalLines,
      totalRows: totalLineColumns,
      subTotalRows: detailLineColumns,
    },
  });

  if (
    [preceding, current].some((summary) => summary.rebate.checkValidCashRebate)
  ) {
    reviewNote +=
      "\n*CIT rebate is not applicable as the computed CIT rebate is less than the $2,000 CIT cash rebate received by the company.";
  }

  return { reviewNote };
};
